import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react"
import { ArrowDownWideNarrow, ArrowUpNarrowWide } from "lucide-react"
import { cn } from "@strivelabs/ui-core"
import { Tooltip } from "@strivelabs/ui-core/v2"
import { nextListTableSort, type ListTableSort } from "./format"
import { ListTableHoverCardProvider } from "./hover-card"
import { StickAtRest } from "./stick-at-rest"

/**
 * How a table lays out its columns.
 *
 * - `fixed`: a heading row, and every column at the width it is given, so the
 *   eye can run down a column. One column has no width and takes the rest.
 * - `fluid`: no heading row, and the columns without a width are as wide as
 *   what they hold, so a row with one label does not keep the room for three.
 *   One column grows into the slack; the ones marked `squeeze` give first when
 *   the row runs short. Order the rows from the Sort menu instead of headings.
 */
export type ListTableType = "fixed" | "fluid"

/**
 * How a fluid table sizes a column that has no `width`.
 *
 * - `grow`: takes what the others leave, down to its `minWidth`. One per table;
 *   without one, the first column with no width grows.
 * - `content`: as wide as what it holds, and never narrower.
 * - `squeeze`: as wide as what it holds until the row runs short, and then the
 *   first to give, all the way down if it has to.
 */
export type ListTableColumnSize = "grow" | "content" | "squeeze"

export interface ListTableColumn<TRow> {
  id: string
  header: ReactNode
  /**
   * The column's width in pixels, the same on every row and in the header, so a
   * reader's eye can hold a column. Leave it off one column — usually the
   * title — and that column takes whatever the fixed ones leave.
   */
  width?: number
  /** The flexible column's floor. Below it the list scrolls sideways instead. */
  minWidth?: number
  /** In a fluid table, how a column with no `width` is sized. */
  size?: ListTableColumnSize
  /**
   * A squeezing column's floor on one row: what it may not give below there.
   * Per row, because a cell with nothing in it should give everything — a row
   * with labels keeps room for its `+N`, a row without keeps none.
   */
  floor?: (row: TRow) => number
  /** Make the heading a sort control. */
  sortable?: boolean
  /**
   * What the sort tooltip calls the column when that differs from the heading —
   * a column headed "Created" is ordered by "created date".
   */
  sortLabel?: string
  /**
   * The words the tooltip uses for the order a click would give. Ascending and
   * Descending unless a column reads better another way — a title's `A-Z`.
   */
  sortWords?: { asc: string; desc: string }
  /** Dropped while the table is lean, for example with a panel open beside it. */
  hideWhenLean?: boolean
  /**
   * Fade the cell out at its right edge instead of cutting what runs past it,
   * so the reader is told there is more rather than shown half a word.
   */
  fade?: boolean
  cell: (row: TRow) => ReactNode
  className?: string
}

export interface ListTableProps<TRow> {
  /**
   * `fixed` (the default) for columns under headings, `fluid` for a list whose
   * rows size their parts to what they hold. See {@link ListTableType}.
   */
  type?: ListTableType
  columns: ListTableColumn<TRow>[]
  rows: TRow[]
  getRowId: (row: TRow) => string
  /** Accessible name for the table. */
  ariaLabel?: string
  /** Accessible name for one row. */
  rowLabel?: (row: TRow) => string
  sort?: ListTableSort | null
  /**
   * Told the order a heading click asks for: descending first, then flipping.
   * Without it no heading is a sort control, whatever its column says.
   */
  onSortChange?: (sort: ListTableSort) => void
  selectedRowId?: string | null
  onRowClick?: (row: TRow) => void
  /** Show only the columns not marked `hideWhenLean`. */
  lean?: boolean
  /**
   * Pin the heading row to the page's scroller where it rests, so it stays put
   * while the rows go past under it.
   */
  stickyHeader?: boolean
  /** Row height in pixels. 40 holds one line; two-line cells want more. */
  rowHeight?: number
  /** What to say when there are no rows. */
  empty?: ReactNode
  className?: string
}

/** Row side padding, the gap between cells, and the list's own inset. */
const ROW_PAD_LEFT = 18
const ROW_PAD_RIGHT = 8
const CELL_GAP = 10
const DEFAULT_FLEX_MIN = 220
const DEFAULT_ROW_HEIGHT = 40

const FADE_EDGE = "[mask-image:linear-gradient(to_right,black_calc(100%-18px),transparent)]"
const FADE_LIST = "[mask-image:linear-gradient(to_right,black_calc(100%-36px),transparent)]"
const SELECTED_RING = "shadow-[inset_0_0_0_2px_hsl(var(--v2-list-selected-ring))]"

/** What the layout helpers read of a column: everything but its cell. */
type ColumnShape = Omit<ListTableColumn<never>, "cell" | "floor">

/**
 * How a fluid table sizes each column: its own `size`, or `grow` for the first
 * column with no width and `content` for the rest. Fixed columns are `null`.
 */
const fluidSizes = (columns: ColumnShape[]): (ListTableColumnSize | null)[] => {
  const marked = columns.some((column) => column.width === undefined && column.size === "grow")
  let grower = marked
  return columns.map((column) => {
    if (column.width !== undefined) return null
    if (column.size) return column.size
    if (grower) return "content"
    grower = true
    return "grow"
  })
}

const fixedStyle = (column: ColumnShape): CSSProperties => (
  column.width === undefined
    ? { flex: "1 1 auto", minWidth: column.minWidth ?? DEFAULT_FLEX_MIN, width: "auto" }
    : { flex: `0 0 ${column.width}px`, width: column.width }
)

/**
 * A fluid column's box. Shrink factors decide who gives when the row runs
 * short: a squeezing column at a hundred against the grower's one takes the
 * whole shortfall while it can, so a title is never cut to keep room the
 * labels beside it could have given up.
 */
const fluidStyle = (column: ColumnShape, size: ListTableColumnSize | null): CSSProperties => {
  if (size === null) return { flex: `0 0 ${column.width}px`, width: column.width }
  if (size === "grow") return { flex: "1 1 auto", minWidth: column.minWidth ?? 0 }
  if (size === "squeeze") return { flex: "0 100 auto", minWidth: column.minWidth ?? 0 }
  return { flex: "0 0 auto" }
}

/** The narrowest a row can be before the list has to scroll sideways. */
const rowMinWidth = (columns: ColumnShape[], type: ListTableType) => columns.reduce(
  (sum, column) => sum + (column.width ?? column.minWidth ?? (type === "fixed" ? DEFAULT_FLEX_MIN : 0)),
  ROW_PAD_LEFT + ROW_PAD_RIGHT + CELL_GAP * Math.max(columns.length - 1, 0),
)

/** The desc and asc marks: the arrow beside bars that narrow the way it points. */
const SortGlyph = ({ direction }: { direction: "asc" | "desc" }) => {
  const Glyph = direction === "desc" ? ArrowDownWideNarrow : ArrowUpNarrowWide
  return <Glyph className="size-[14px]" strokeWidth={1.8} aria-hidden />
}

const headingText = (column: ColumnShape) =>
  column.sortLabel ?? (typeof column.header === "string" ? column.header : column.id)

/**
 * What the heading's tooltip says: the column to order by when it is not the
 * one sorted, and the order a click would give when it is.
 */
const sortTip = (column: ColumnShape, heading: string, sort: ListTableSort | null | undefined) => {
  if (sort?.columnId !== column.id) return `Order by ${heading}`
  const words = column.sortWords ?? { asc: "Ascending", desc: "Descending" }
  return sort.direction === "desc" ? words.asc : words.desc
}

/**
 * The platform's list table: fixed-width columns on borderless rows, sorted from
 * the headings, a row opening on click, and only the rows scrolling sideways.
 *
 * Rows rather than a `<table>`: each row is one rounded box that a hover tints
 * and an open row rings from the inside, which a table row cannot draw. Every
 * row and the heading lay out the same boxes at the same widths, which is what
 * keeps the columns aligned.
 *
 * The heading lives in a scroller of its own, outside the rows, so it can be
 * pinned to the page while the rows scroll sideways under it — a sticky header
 * inside a sideways scroller would stick to that scroller instead. Its
 * `scrollLeft` follows the rows'.
 *
 * Presentational and controlled: the caller holds the rows, the order and the
 * open row, so a server-sorted list and a client-sorted one read the same.
 *
 * Two layouts on the same rows (`type`): `fixed` columns under headings, and a
 * `fluid` list without headings whose parts are as wide as what they hold.
 */
export function ListTable<TRow> ({
  type = "fixed",
  columns,
  rows,
  getRowId,
  ariaLabel = "Table",
  rowLabel,
  sort,
  onSortChange,
  selectedRowId,
  onRowClick,
  lean = false,
  stickyHeader = false,
  rowHeight = DEFAULT_ROW_HEIGHT,
  empty = "Nothing to show",
  className,
}: ListTableProps<TRow>) {
  const shown = lean ? columns.filter((column) => !column.hideWhenLean) : columns
  const minWidth = rowMinWidth(shown, type)
  const fluid = type === "fluid"
  const sizes = fluid ? fluidSizes(shown) : []
  const columnStyle = (column: ColumnShape, index: number) =>
    (fluid ? fluidStyle(column, sizes[index]) : fixedStyle(column))
  const bodyRef = useRef<HTMLDivElement>(null)
  const headRef = useRef<HTMLDivElement>(null)
  const [ moreRight, setMoreRight ] = useState(false)

  const sync = useCallback(() => {
    const body = bodyRef.current
    if (!body) return
    if (headRef.current) headRef.current.scrollLeft = body.scrollLeft
    setMoreRight(body.scrollWidth - body.clientWidth - body.scrollLeft > 1)
  }, [])

  useEffect(() => {
    sync()
    const body = bodyRef.current
    if (!body || typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(sync)
    observer.observe(body)
    return () => observer.disconnect()
  }, [ sync, minWidth, rows.length ])

  // A heading only sorts when someone is listening for the order it asks for.
  const sortable = Boolean(onSortChange)
  const clickable = Boolean(onRowClick)
  const onKey = (event: KeyboardEvent, row: TRow) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onRowClick?.(row)
    }
  }

  return (
    <ListTableHoverCardProvider>
      <div role="table" aria-label={ariaLabel} data-type={type} className={cn("min-w-0", className)}>
        {/* A fluid list has no heading row: its parts are not columns the eye
            runs down, and its order comes from the Sort menu. */}
        {fluid ? null : <StickAtRest
          ref={headRef}
          sticky={stickyHeader}
          className={cn("overflow-hidden px-1.5", stickyHeader && "z-[1] bg-v2-card", moreRight && FADE_LIST)}
        >
          <div
            role="row"
            className="mt-1.5 flex h-10 items-center gap-x-2.5 pl-[18px] pr-2"
            style={{ minWidth }}
          >
            {shown.map((column, index) => {
              const sorted = sort?.columnId === column.id
              const heading = headingText(column).toLowerCase()
              return (
                <span
                  key={column.id}
                  role="columnheader"
                  aria-sort={sorted ? (sort.direction === "asc" ? "ascending" : "descending") : undefined}
                  className={cn(
                    "flex min-w-0 items-center overflow-hidden whitespace-nowrap",
                    "text-[14px] font-[450] leading-[1.5] text-v2-list-head",
                  )}
                  style={columnStyle(column, index)}
                >
                  {column.sortable && sortable ? (
                    <Tooltip label={sortTip(column, heading, sort)}>
                      <button
                        type="button"
                        aria-label={`Sort by ${heading}`}
                        className={cn(
                          "group/hcol -ml-1.5 inline-flex shrink-0 items-center gap-1.5 rounded-v2-md px-1.5 py-1",
                          "text-v2-list-head outline-none transition-colors duration-v2-quick ease-v2-out",
                          "hover:text-v2-list-ink focus-visible:text-v2-list-ink",
                        )}
                        onClick={() => onSortChange?.(nextListTableSort(sort, column.id))}
                      >
                        {column.header}
                        <span
                          aria-hidden
                          className={cn(
                            "inline-flex shrink-0 text-v2-list-faint transition-opacity duration-v2-quick ease-v2-out",
                            "group-hover/hcol:text-v2-list-ink group-focus-visible/hcol:text-v2-list-ink",
                            sorted ? "opacity-100" : "opacity-0 group-hover/hcol:opacity-100 group-focus-visible/hcol:opacity-100",
                          )}
                        >
                          <SortGlyph direction={sorted ? sort.direction : "desc"} />
                        </span>
                      </button>
                    </Tooltip>
                  ) : column.header}
                </span>
              )
            })}
          </div>
        </StickAtRest>}

        <div
          ref={bodyRef}
          role="rowgroup"
          className={cn("overflow-x-auto overscroll-x-contain px-1.5 pb-1.5", fluid && "pt-0.5", moreRight && FADE_LIST)}
          onScroll={sync}
        >
          {rows.length === 0 ? (
            <div className="px-2.5 pb-[18px] pt-3.5 text-[13px] leading-[1.5] text-v2-list-faint">{empty}</div>
          ) : rows.map((row) => {
            const id = getRowId(row)
            const selected = id === selectedRowId
            return (
              <div
                key={id}
                role="row"
                tabIndex={clickable ? 0 : undefined}
                aria-label={rowLabel?.(row)}
                aria-selected={clickable ? selected : undefined}
                data-state={selected ? "selected" : undefined}
                onClick={clickable ? () => onRowClick?.(row) : undefined}
                onKeyDown={clickable ? (event) => onKey(event, row) : undefined}
                className={cn(
                  "flex items-center gap-x-2.5 rounded-v2-lg pl-[18px] pr-2",
                  "transition-[background-color,box-shadow] duration-v2-quick ease-v2-out motion-reduce:transition-none",
                  clickable && "cursor-pointer focus-visible:outline-none",
                  clickable && !selected && "focus-visible:shadow-[inset_0_0_0_2px_hsl(var(--v2-list-selected-ring))]",
                  selected ? cn("bg-v2-list-selected", SELECTED_RING) : "hover:bg-v2-list-hover",
                )}
                style={{ minWidth, height: rowHeight }}
              >
                {shown.map((column, index) => (
                  <div
                    key={column.id}
                    role="cell"
                    className={cn(
                      "flex min-w-0 items-center overflow-hidden",
                      column.fade && FADE_EDGE,
                      column.className,
                    )}
                    style={column.floor && sizes[index] === "squeeze"
                      ? { ...columnStyle(column, index), minWidth: column.floor(row) }
                      : columnStyle(column, index)}
                  >
                    {column.cell(row)}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </ListTableHoverCardProvider>
  )
}
